import {Module} from '@fantasmagames/slot/src/common/Module';
import {Settings} from '@fantasmagames/slot/src/common/settings/Settings';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {InfoEvents} from '@fantasmagames/slot/src/modules/hud/info/InfoEvents';
import {MainMenuEvents} from '@fantasmagames/slot/src/modules/mainmenu/MainMenuEvents';
import {SettingsEvents} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsEvents';
import {ISettingsData, SettingsView} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsView';
import {SettingsViewEvents} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsViewEvents';

export class SettingsModule extends Module<SettingsView> {
	public init(): void {
		super.init();

		const savedData: ISettingsData = {
			soundActive: this.logic.soundEffectsActive,
			musicActive: this.logic.musicActive,
			skipIntroActive: this.logic.skipIntro,
			spacebarActive: this.logic.spacebarSpinActive,
			dragReelToSpinActive: this.logic.dragReelToSpinActive,
			quickStopActive: this.logic.quickStopActive,
			fastPlayActive: this.logic.fastPlayActive,
		};

		const extraButtons = {
			responsibleGaming: this.logic.responsibleGaming,
		};

		this.view.setupSettingsViewComponents(savedData);
	}

	protected setupStates(): void {}

	protected enterFirstState(): void {}

	protected setupViews(): void {
		if (this.system.shouldUseMultipleViews()) {
			this.setView(Views.SETTINGS_VIEW_MOBILE);
		} else {
			this.setView(Views.SETTINGS_VIEW);
		}
	}

	protected showSettings(): void {
		this.view.showSettings();
	}

	protected closeSettings(): void {
		this.view.hideSettings();
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.on(MainMenuEvents.MAIN_MENU_SETTINGS_OPEN, this.onMainMenuSettingsOpen);
		this.on(MainMenuEvents.MAIN_MENU_SETTINGS_CLOSE, this.onMainMenuSettingsClosed);
		this.on(MainMenuEvents.MAIN_MENU_CLOSED, this.onMainMenuClosed);
		this.on(MainMenuEvents.MAIN_MENU_INFO_OPEN, this.onMainMenuInfoOpen);
		this.on(MainMenuEvents.MAIN_MENU_PAYTABLE_OPEN, this.onMainMenuPaytableOpen);

		this.on(InfoEvents.TOGGLE_ALL_SOUND_OFF, this.onInfoAllSoundOff);
		this.on(InfoEvents.TOGGLE_ALL_SOUND_ON, this.onInfoAllSoundOn);

		this.onView(SettingsViewEvents.TOGGLE_SOUND_OFF, this.onSoundOff);
		this.onView(SettingsViewEvents.TOGGLE_SOUND_ON, this.onSoundOn);

		this.onView(SettingsViewEvents.TOGGLE_MUSIC_OFF, this.onMusicOff);
		this.onView(SettingsViewEvents.TOGGLE_MUSIC_ON, this.onMusicOn);

		this.onView(SettingsViewEvents.TOGGLE_SPACEBAR_OFF, this.onSpacebarOff);
		this.onView(SettingsViewEvents.TOGGLE_SPACEBAR_ON, this.onSpacebarOn);

		this.onView(SettingsViewEvents.TOGGLE_DRAG_REEL_TO_SPIN_OFF, this.onDragReelToSpinOff);
		this.onView(SettingsViewEvents.TOGGLE_DRAG_REEL_TO_SPIN_ON, this.onDragReelToSpinOn);

		this.onView(SettingsViewEvents.TOGGLE_SKIP_INTRO_OFF, this.onSkipIntroOff);
		this.onView(SettingsViewEvents.TOGGLE_SKIP_INTRO_ON, this.onSkipIntroOn);

		this.onView(SettingsViewEvents.TOGGLE_FAST_PLAY_OFF, this.onFastPlayOff);
		this.onView(SettingsViewEvents.TOGGLE_FAST_PLAY_ON, this.onFastPlayOn);
	}

	protected onFastPlayOn(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_FAST_PLAY_ON);
		this.logic.fastPlayActive = true;
		this.settings.add(Settings.QUICKSPIN, true);

		this.saveData();
	}

	protected onFastPlayOff(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_FAST_PLAY_OFF);
		this.logic.fastPlayActive = false;
		this.settings.add(Settings.QUICKSPIN, false);

		this.saveData();
	}

	protected onMainMenuSettingsOpen(): void {
		this.showSettings();
	}

	protected onMainMenuClosed(): void {
		this.closeSettings();
	}

	protected onMainMenuSettingsClosed(): void {
		this.closeSettings();
	}

	protected onMainMenuInfoOpen(): void {
		this.closeSettings();
	}

	protected onMainMenuPaytableOpen(): void {
		this.closeSettings();
	}

	protected isSoundSettingsOff(): void {
		const musicActive = this.logic.musicActive;
		const effectsActive = this.logic.soundEffectsActive;

		const soundAllActive = this.logic.soundAllActive;

		if (!musicActive && !effectsActive && soundAllActive) {
			this.emitAllSoundOff();
		}
	}

	protected emitAllSoundOn(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_ALL_SOUND_ON);
	}

	protected emitAllSoundOff(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_ALL_SOUND_OFF);
	}

	protected onInfoAllSoundOff(eventId: string, clicked: boolean = true): void {
		this.onAllSoundOff(null, clicked);
	}

	protected onInfoAllSoundOn(eventId: string, clicked: boolean = true): void {
		this.onAllSoundOn(null, clicked);
	}

	protected onAllSoundOn(eventId: string = null, clicked: boolean = true): void {
		this.soundPlayer.enableAllSound();
		this.settings.add(Settings.SOUNDS_ON, true);
		this.settings.add(Settings.MUSIC_ON, true);

		this.logic.soundAllActive = true;

		if (clicked) {
			this.view.allSoundsOn();
		}

		this.saveData();
	}

	protected onAllSoundOff(eventId: string = null, clicked: boolean = true): void {
		this.soundPlayer.disableAllSound();
		this.settings.add(Settings.SOUNDS_ON, false);
		this.settings.add(Settings.MUSIC_ON, false);

		this.logic.soundAllActive = false;

		if (clicked) {
			this.view.allSoundsOff();
		}

		this.saveData();
	}

	protected onSoundOn(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_SOUND_EFFECTS_ON);
		this.soundPlayer.enableSounds();
		this.settings.add(Settings.SOUNDS_ON, true);
		this.logic.soundEffectsActive = true;
		// this.isSoundSettingsOff();

		this.saveData();
	}

	protected onSoundOff(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_SOUND_EFFECTS_OFF);
		this.soundPlayer.disableSounds();
		this.settings.add(Settings.SOUNDS_ON, false);

		this.logic.soundEffectsActive = false;
		// this.isSoundSettingsOff();

		this.saveData();
	}

	protected onMusicOn(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_MUSIC_ON);
		this.soundPlayer.enableMusic();
		this.settings.add(Settings.MUSIC_ON, true);

		this.logic.musicActive = true;
		// this.isSoundSettingsOff();

		this.saveData();
	}

	protected onMusicOff(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_MUSIC_OFF);
		this.soundPlayer.disableMusic();
		this.settings.add(Settings.MUSIC_ON, false);

		this.logic.musicActive = false;
		// this.isSoundSettingsOff();

		this.saveData();
	}

	protected onSpacebarOn(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_SPACEBAR_ON);
		this.logic.spacebarSpinActive = true;
		this.saveData();
	}

	protected onSpacebarOff(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_SPACEBAR_OFF);
		this.logic.spacebarSpinActive = false;
		this.saveData();
	}

	protected onDragReelToSpinOn(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_DRAG_REEL_TO_SPIN_ON);
		this.logic.dragReelToSpinActive = true;
		this.saveData();
	}

	protected onDragReelToSpinOff(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_DRAG_REEL_TO_SPIN_OFF);
		this.logic.dragReelToSpinActive = false;
		this.saveData();
	}

	protected onSkipIntroOn(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_SKIP_INTRO_ON);
		this.logic.skipIntro = true;
		this.saveData();
	}

	protected onSkipIntroOff(): void {
		this.eventManager.emit(SettingsEvents.TOGGLE_SKIP_INTRO_OFF);
		this.logic.skipIntro = false;
		this.saveData();
	}

	protected saveData(): void {
		if (this.canSaveData) {
			this.logic.saveData();
		}
	}

	protected get canSaveData(): boolean {
		return true;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsModule.ts